import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiUrl } from './constants';
import {
  IRemoteSalesAccesses,
  RequestParams,
} from '@src/app/shared/types/common';
import { RoutesEnum } from '@src/app/enums';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class OnlineApplicationsService {
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  private currentApplicationSubject = new BehaviorSubject<Record<string, any>>(
    {},
  );
  currentApplication$ = this.currentApplicationSubject.asObservable();

  private applicationFormDraftSubject = new BehaviorSubject<Record<
    string,
    any
  > | null>(null);
  applicationFormDraft$ = this.applicationFormDraftSubject.asObservable();

  private isFromProductSubject = new BehaviorSubject<boolean>(false);
  isFromProduct$ = this.isFromProductSubject.asObservable();

  private salesTypeIdSubject = new BehaviorSubject<number | undefined>(
    undefined,
  );
  salesTypeId$ = this.salesTypeIdSubject.asObservable();

  private remoteSalesAccessesSubject = new BehaviorSubject<
    Record<string, string> | undefined
  >(undefined);
  remoteSalesAccesses$ = this.remoteSalesAccessesSubject.asObservable();

  private salesApplicationResponseDataSubject = new BehaviorSubject<
    Record<string, any>
  >({});
  salesApplicationResponseData$ =
    this.salesApplicationResponseDataSubject.asObservable();

  constructor(
    @Inject(Router) private router: Router,
    private apiService: ApiService,
  ) {}

  get currentApplication(): Record<string, any> {
    return this.currentApplicationSubject.value;
  }

  get applicationFormDraft(): Record<string, any> | null {
    return this.applicationFormDraftSubject.value;
  }

  get isFromProduct(): boolean {
    return this.isFromProductSubject.value;
  }

  get remoteSalesAccesses(): Record<string, string> | undefined {
    return this.remoteSalesAccessesSubject.value;
  }

  get salesApplicationResponseData(): Record<string, any> {
    return this.salesApplicationResponseDataSubject.value;
  }

  get salesTypeId(): number | undefined {
    return this.salesTypeIdSubject.value;
  }

  setIsLoading(value: boolean): void {
    this.isLoadingSubject.next(value);
  }

  setCurrentApplication(payload: Record<string, any>): void {
    this.currentApplicationSubject.next(payload);
  }

  setApplicationFormDraft(payload: Record<string, any> | null): void {
    this.applicationFormDraftSubject.next(payload);
  }

  setIsFromProduct(value: boolean): void {
    this.isFromProductSubject.next(value);
  }

  setRemoteSalesAccesses(data: Record<string, string> | undefined): void {
    this.remoteSalesAccessesSubject.next(data);
  }

  setSalesApplicationResponseData(data: Record<string, any>): void {
    this.salesApplicationResponseDataSubject.next(data);
  }

  setSalesTypeId(value: number | undefined): void {
    this.salesTypeIdSubject.next(value);
  }

  handleError(error: HttpErrorResponse): void {
    this.isLoadingSubject.next(false);

    if (
      error.error?.statusCode === 404 ||
      error.error?.status === 404 ||
      error?.status === 404
    ) {
      this.router.navigate(['/' + RoutesEnum.NOT_FOUND], {
        skipLocationChange: true,
      });
    } else if (error.error?.message) {
      console.error(error.error?.message);
    }
  }

  getOnlineApplicationsPage(
    params: RequestParams,
  ): Observable<Record<string, any>[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getOnlineApplicationsPage,
      params,
    });
  }

  getOnlineApplicationsRequest(
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getOnlineApplicationsRequest,
      params,
    });
  }

  getOnlineApplicationBySlug(
    slug: number | string,
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getOnlineApplication(slug),
      params,
    });
  }

  getOnlineApplicationSpecialBySlug(
    slug: number | string,
    params: RequestParams,
    headers: Record<string, string>,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getOnlineApplicationSpecial(slug),
      params,
      headers,
    });
  }

  postOnlineApplicationsForm(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.postOnlineApplicationsForm,
      params,
      requestFormat: 'form',
      body,
    });
  }

  postOnlineApplicationSpecialForm(
    body: Record<string, any>,
    params?: any,
    headers?: Record<string, string>,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.postOnlineApplicationSpecialForm,
      params,
      requestFormat: 'form',
      body,
      headers,
    });
  }
}
